/**
 * Camera framing derived from the baked dataset.
 *
 * `flyToCountry` and `orientToRegion` both need a centre and a zoom. Deriving
 * them here — from the same data the polygons come from — keeps a hard-coded
 * table of magic camera positions out of the component, and means a new
 * country or region in the bake is framed correctly with no code change.
 */

import { allCountries, byCca3, featureFor } from '@geogame/data';

import type { LngLatTuple } from './geo';
import {
  angularSpreadDeg,
  lngLatOf,
  representativePoint,
  sampleVertices,
  sphericalMean,
  zoomForAngularRadius,
} from './geo';
import { CAMERA } from './theme';

export interface Framing {
  readonly center: LngLatTuple;
  readonly zoom: number;
}

const countryCache = new Map<string, Framing>();
let regionCache: ReadonlyMap<string, Framing> | null = null;

/** Case- and punctuation-insensitive key so "South-Eastern Asia" ≈ "southeastern asia". */
function key(s: string): string {
  return s.toLowerCase().replace(/[^a-z]/g, '');
}

/**
 * Where to put the camera to show one country.
 *
 * The centre comes from the dataset's curated `latlng`, never from a polygon
 * bounding box: Russia and Fiji straddle the antimeridian and a naive bbox
 * centre drops the camera in the wrong ocean. The one exception is a curated
 * point that is not inside its own country — see below. The polygon is used
 * only to estimate how wide the country is, via great-circle distances, which
 * wrap correctly.
 */
export function frameForCountry(cca3: string): Framing | null {
  const code = cca3.toUpperCase();
  const cached = countryCache.get(code);
  if (cached) return cached;

  const record = byCca3(code);
  if (!record) return null;

  let center = lngLatOf(record);
  const feature = featureFor(code);

  let radiusDeg: number;
  if (feature) {
    const vertices = sampleVertices(feature.geometry);
    // Nine curated centres do not lie inside their own polygon — Kuwait's sits
    // in Iraq and Israel's in the West Bank — and flying there frames the
    // wrong country. `representativePoint` returns the curated point untouched
    // when it is fine, and an interior point otherwise.
    center =
      representativePoint(feature.geometry, center) ??
      (vertices.length > 0 ? sphericalMean(vertices) : center);
    radiusDeg = angularSpreadDeg(center, vertices, 0.9);
  } else {
    // No polygon: infer a radius from land area, floored so microstates still
    // get a sane, close-but-not-absurd zoom.
    radiusDeg = Math.max(0.6, Math.sqrt(Math.max(record.area, 1)) / 111);
  }

  const framing: Framing = {
    center,
    zoom: zoomForAngularRadius(radiusDeg * 1.35, CAMERA.minZoom, CAMERA.maxZoom),
  };
  countryCache.set(code, framing);
  return framing;
}

function buildRegionIndex(): ReadonlyMap<string, Framing> {
  const groups = new Map<string, LngLatTuple[]>();

  const add = (name: string, p: LngLatTuple): void => {
    const k = key(name);
    if (!k) return;
    const list = groups.get(k);
    if (list) list.push(p);
    else groups.set(k, [p]);
  };

  for (const c of allCountries()) {
    if (c.isDisputed) continue;
    const p = lngLatOf(c);
    add(c.region, p);
    add(c.subregion, p);
  }

  const index = new Map<string, Framing>();
  for (const [k, points] of groups) {
    const center = sphericalMean(points);
    const radius = angularSpreadDeg(center, points, 0.9);
    index.set(k, {
      center,
      zoom: zoomForAngularRadius(Math.max(radius * 1.35, 6), CAMERA.minZoom, 4),
    });
  }

  // Common names the dataset does not use verbatim.
  const alias: Record<string, string> = {
    middleeast: 'westernasia',
    neareast: 'westernasia',
    subsaharanafrica: 'africa',
    latinamerica: 'southamerica',
    centralandsouthamerica: 'southamerica',
    balkans: 'southeasteurope',
    scandinavia: 'northerneurope',
    nordics: 'northerneurope',
    pacific: 'oceania',
    world: '',
  };
  for (const [from, to] of Object.entries(alias)) {
    const target = index.get(to);
    if (target && !index.has(from)) index.set(from, target);
  }

  return index;
}

/** Camera framing for a region or subregion name, or null when unknown. */
export function frameForRegion(region: string): Framing | null {
  regionCache ??= buildRegionIndex();
  return regionCache.get(key(region)) ?? null;
}

/**
 * Where to point the camera for Explorer's region hint, at the scale the match
 * already opened on.
 *
 * `frameForRegion` answers "what camera SHOWS this whole region", and that is
 * the wrong question for the hint. The server only ever sends a top-level
 * region for the hint, so the name is almost always a continent: Africa spans
 * roughly 35 degrees of arc, `frameForRegion` widens that to about zoom 1.6,
 * and the home zoom on a desktop viewport sits nearer 2.2. Orienting therefore
 * pulled the globe BACKWARDS on every Explorer round, which is the bug the
 * owner reported as "we zoom out from the default zoom".
 *
 * The hint's whole job is to turn the planet so the right part of it is facing
 * the player. Scale is not part of that job, so the caller's zoom is passed
 * straight through and only the centre comes from the region index. A round
 * now opens at exactly the size the match started at, every time.
 */
export function orientFramingFor(region: string, currentZoom: number): Framing | null {
  const framing = frameForRegion(region);
  if (!framing) return null;
  return { center: framing.center, zoom: currentZoom };
}

/** Every region/subregion key `orientToRegion` accepts. Used by the harness. */
export function knownRegionKeys(): readonly string[] {
  regionCache ??= buildRegionIndex();
  return [...regionCache.keys()].sort();
}
